import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CAMPO_BASE, CampoDeBusca } from "./ui";
import { BarraDeFiltros, LimparFiltros, LinhaDeFiltros } from "./BarraDeFiltros";
import { AvisoDaLeitura, LinhaDoPeriodo, SeloDaMedia, comPonto, fraseSolta } from "./AdminAvaliacoesUi";
import DialogoDaAvaliacao from "./AdminAvaliacoesDialogo";
import { numero } from "../lib/admin";
import { rolarAte } from "../lib/rolarPara";
import { artigoDaHora } from "../lib/horaNoTexto";
import {
  AINDA_SEM_AVALIACAO,
  FAIXAS,
  ROTULO_DA_ORIGEM,
  TETO_DA_PLANILHA,
  buscaSoDoPeriodo,
  chaveDoPedido,
  chaveDoPeriodo,
  lerAvaliacoes,
  planilhaDaLista,
  quandoLegivel,
  useLeitura,
  type AvaliacaoDaLista,
  type FiltrosDaLista,
  type ListaDeAvaliacoes,
  type OrdemDaLista,
  type PedidoDePeriodo,
} from "../lib/avaliacoes";

/**
 * Admin › Avaliações › Feedbacks (000328): cada avaliação recebida, com filtros,
 * a planilha e o detalhe de uma.
 *
 * É a lista «Feedbacks» do painel antigo (`feedbacks-cf`, a Central Feedbacks
 * que o time de Produtos lia), que Victor pediu de volta em 29/09 — *"Feedbacks —
 * lista de todos os feedbacks recebidos com filtros"*. O CONTEÚDO veio de lá: a
 * busca no comentário e no nome da ferramenta, a ferramenta, a média mínima, a
 * planilha, a paginação com o tamanho da página e o detalhe ao clicar. O que
 * mudou, e por quê:
 *
 *   · **o filtro é do BANCO** (`av_admin_avaliacoes`). Lá o navegador baixava
 *     TODAS as avaliações e filtrava em JavaScript; aqui o filtro, a ordem, o
 *     corte e as duas contagens vêm prontos. Filtrar na tela seria a segunda
 *     definição de "baixa" e de "no período" — e o período é um dia de São
 *     Paulo, que o relógio do navegador não sabe qual é;
 *   · **não há coluna de quem escreveu.** Lá a lista mostrava o e-mail do aluno;
 *     aqui a equipe vê nota e comentário, nunca a pessoa (a regra das pesquisas),
 *     e o banco nem devolve;
 *   · **os filtros moram na URL**, junto do período: é o que deixa mandar a um
 *     colega o link de "as baixas do Radar nos últimos 7 dias", e é o que faz o
 *     botão de voltar do navegador, depois de abrir a página de uma ferramenta,
 *     trazer a lista com os mesmos filtros e na mesma página. Cada troca de
 *     filtro SUBSTITUI a entrada do histórico em vez de criar outra (ver
 *     `mudar`), então voltar não percorre letra a letra o que foi digitado;
 *   · entraram o filtro de COMENTÁRIO (com, sem), a ORDEM, o "Só as baixas" e a
 *     coluna de ORIGEM (o hub ou o app antigo).
 *
 * ---------------------------------------------------------------------------
 * A tela DECLARA o que o banco respondeu
 * ---------------------------------------------------------------------------
 * Entre o clique num filtro e a resposta, a lista antiga continua de pé (mais
 * apagada), e quem espera "a lista mudou" sem olhar o filtro lê a antiga — a
 * cicatriz da busca da Central, 14/09. Então a chave dos filtros que foram
 * MANDADOS viaja junto da resposta (`filtrosMandados`), e a raiz a declara em
 * `data-filtros-dos-dados`; o que a URL pede agora está em `data-filtros-pedidos`.
 * As duas são a busca da URL só com os filtros desta sub-aba, sem os valores
 * padrão, na ordem card, busca, media, comentario, ordem, pagina e por (vazia
 * quando não há filtro nenhum). Quando as duas são iguais e `data-painel-pronto`
 * é "true", a lista é a do pedido de agora. O mesmo vale para o período
 * (`data-periodo-dos-dados` contra `data-periodo-pedido`).
 *
 * ---------------------------------------------------------------------------
 * ZERO conta aqui
 * ---------------------------------------------------------------------------
 * A média de cada avaliação, "no período", as contagens, a ordem e o corte são do
 * banco. A tela lê a URL, formata e desenha. As faixas de `FAIXAS` só PINTAM o
 * selo — e o "Só as baixas" manda ao banco o MESMO limite de lá (5), para o
 * filtro e o selo não discordarem.
 */

// ── Os filtros, como a URL os guarda ─────────────────────────────────────────

type FaixaDoFiltro = "" | "9" | "8" | "7" | "5" | "baixas";
type ComentarioDoFiltro = "" | "com" | "sem";
type PorPagina = 10 | 25 | 50 | 100;

/** O que a URL pede. Separado de `FiltrosDaLista` (o que vai ao banco) porque a URL fala em faixa e em página, e o banco em mínimo, máximo e deslocamento. */
type FiltrosDaTela = {
  card: string | null;
  busca: string;
  media: FaixaDoFiltro;
  comentario: ComentarioDoFiltro;
  ordem: OrdemDaLista;
  /** De 1. */
  pagina: number;
  por: PorPagina;
};

const PADRAO: FiltrosDaTela = {
  card: null,
  busca: "",
  media: "",
  comentario: "",
  ordem: "recentes",
  pagina: 1,
  por: 25,
};

/** As chaves desta sub-aba na URL. As do período (`periodo`, `de`, `ate`) são do despacho e não são tocadas aqui. */
const CHAVES_DA_URL = ["card", "busca", "media", "comentario", "ordem", "pagina", "por"] as const;

/**
 * As faixas do filtro de média, as do painel antigo (média ≥ 0, 5, 7, 8 e 9) e
 * mais uma que lá não existia: "Só as baixas". O limite dela sai de
 * `FAIXAS.baixaAte`, que é o mesmo número com que o selo pinta uma média de
 * baixa — e o mesmo que o banco usa para CONTAR as baixas: com dois números,
 * o filtro traria selo que não é de baixa.
 *
 * Cada rótulo diz "Média" de novo porque o combo fechado mostra UMA opção, sem
 * as outras em volta: "8 ou mais" sozinho não diz de quê.
 *
 * "Só as baixas (até 5)", e não "(5 ou menos)", desde 29/09: o combo tem a
 * largura da opção mais longa, e era esta — na barra de 1280px ele sobrava
 * largo enquanto a busca cortava o próprio texto de exemplo em "Comentário ou
 * fer". "Até 5" diz o mesmo limite, com o 5 dentro, em cinco caracteres a menos.
 */
const MEDIAS: { valor: FaixaDoFiltro; rotulo: string; min: number | null; max: number | null }[] = [
  { valor: "", rotulo: "Qualquer média", min: null, max: null },
  { valor: "9", rotulo: "Média 9 ou mais", min: 9, max: null },
  { valor: "8", rotulo: "Média 8 ou mais", min: 8, max: null },
  { valor: "7", rotulo: "Média 7 ou mais", min: 7, max: null },
  { valor: "5", rotulo: "Média 5 ou mais", min: 5, max: null },
  { valor: "baixas", rotulo: `Só as baixas (até ${FAIXAS.baixaAte})`, min: null, max: FAIXAS.baixaAte },
];

const COMENTARIOS: { valor: ComentarioDoFiltro; rotulo: string }[] = [
  { valor: "", rotulo: "Com e sem comentário" },
  { valor: "com", rotulo: "Só com comentário" },
  { valor: "sem", rotulo: "Só sem comentário" },
];

/** A ordem por extenso: no combo fechado, "Menor média" sozinho se leria como um filtro. */
const ORDENS: { valor: OrdemDaLista; rotulo: string }[] = [
  { valor: "recentes", rotulo: "Mais recentes primeiro" },
  { valor: "antigas", rotulo: "Mais antigas primeiro" },
  { valor: "menor_media", rotulo: "Menor média primeiro" },
  { valor: "maior_media", rotulo: "Maior média primeiro" },
];

/** Os tamanhos de página do painel antigo. */
const POR_PAGINA: readonly PorPagina[] = [10, 25, 50, 100];

/**
 * Um teto para a página que vem da URL. Sem ele, `pagina=99999999999` viraria um
 * deslocamento que não cabe no inteiro do parâmetro do banco, a chamada
 * estouraria, e a tela diria "as avaliações não vieram" em vez de "esta página
 * não existe". Cem mil páginas de 100 são dez milhões de avaliações; a casa tem
 * 6,6 mil.
 */
const PAGINA_MAXIMA = 100_000;

/**
 * O tempo sem tecla antes de a busca ir para a URL (e dali para o banco). Sem a
 * espera, "calendario" seriam dez leituras, e a resposta que chegasse por
 * último poderia ser a de "cal".
 */
const ESPERA_DA_BUSCA = 300;

/**
 * A leitura da URL. Valor desconhecido volta ao PADRÃO, e não a um erro: um link
 * antigo, ou digitado à mão, abre a lista inteira em vez de uma tela de recusa.
 * O `card` vai em minúsculas porque é assim que o banco devolve os slugs — senão
 * `?card=Radar-Simples` não casaria com a opção do seletor.
 */
function filtrosDaBusca(b: URLSearchParams): FiltrosDaTela {
  const media = b.get("media") ?? "";
  const comentario = b.get("comentario") ?? "";
  const ordem = b.get("ordem") ?? "";
  const pagina = Number(b.get("pagina"));
  const por = Number(b.get("por"));
  return {
    card: (b.get("card") ?? "").trim().toLowerCase() || null,
    busca: (b.get("busca") ?? "").trim(),
    media: MEDIAS.find((m) => m.valor === media)?.valor ?? PADRAO.media,
    comentario: COMENTARIOS.find((c) => c.valor === comentario)?.valor ?? PADRAO.comentario,
    ordem: ORDENS.find((o) => o.valor === ordem)?.valor ?? PADRAO.ordem,
    pagina: Number.isInteger(pagina) && pagina >= 1 && pagina <= PAGINA_MAXIMA ? pagina : PADRAO.pagina,
    por: POR_PAGINA.find((n) => n === por) ?? PADRAO.por,
  };
}

/**
 * Só os filtros desta sub-aba, sem os valores padrão e numa ordem FIXA. É o que
 * vai para a URL e é a CHAVE dos filtros (`chaveDosFiltros`): com a ordem fixa,
 * dois caminhos até o mesmo filtro dão a mesma chave.
 */
function soOsFiltros(f: FiltrosDaTela): URLSearchParams {
  const b = new URLSearchParams();
  if (f.card) b.set("card", f.card);
  if (f.busca) b.set("busca", f.busca);
  if (f.media) b.set("media", f.media);
  if (f.comentario) b.set("comentario", f.comentario);
  if (f.ordem !== PADRAO.ordem) b.set("ordem", f.ordem);
  if (f.pagina !== PADRAO.pagina) b.set("pagina", String(f.pagina));
  if (f.por !== PADRAO.por) b.set("por", String(f.por));
  return b;
}

function chaveDosFiltros(f: FiltrosDaTela): string {
  return soOsFiltros(f).toString();
}

/** A URL nova: o que não é desta sub-aba (o período) fica como estava. */
function buscaComFiltros(base: URLSearchParams, f: FiltrosDaTela): URLSearchParams {
  const nova = new URLSearchParams(base);
  for (const k of CHAVES_DA_URL) nova.delete(k);
  for (const [k, v] of soOsFiltros(f)) nova.set(k, v);
  return nova;
}

function filtrosParaOBanco(f: FiltrosDaTela): FiltrosDaLista {
  const faixa = MEDIAS.find((m) => m.valor === f.media) ?? MEDIAS[0];
  return {
    card: f.card,
    busca: f.busca,
    mediaMin: faixa.min,
    mediaMax: faixa.max,
    comentario: f.comentario === "com" ? true : f.comentario === "sem" ? false : null,
    ordem: f.ordem,
    limite: f.por,
    offset: (f.pagina - 1) * f.por,
  };
}

/** Os filtros sem a paginação: a planilha pagina sozinha, de mil em mil, pela MESMA função. */
function semPaginacao(f: FiltrosDaLista): Omit<FiltrosDaLista, "limite" | "offset"> {
  return {
    card: f.card,
    busca: f.busca,
    mediaMin: f.mediaMin,
    mediaMax: f.mediaMax,
    comentario: f.comentario,
    ordem: f.ordem,
  };
}

/** Os que estreitam a lista. A ordem não estreita: com a lista vazia, "com esses filtros" não seria verdade por causa dela. */
function haFiltro(f: FiltrosDaTela): boolean {
  return Boolean(f.card || f.busca || f.media || f.comentario);
}

/** O que «Limpar filtros» desfaz: os filtros e a ordem — os controles da barra. O tamanho da página fica: é gosto de quem lê, não filtro. */
function haMudancaNaBarra(f: FiltrosDaTela): boolean {
  return haFiltro(f) || f.ordem !== PADRAO.ordem;
}

function plural(n: number, uma: string, varias: string): string {
  return `${numero(n)} ${n === 1 ? uma : varias}`;
}

// ── A sub-aba ───────────────────────────────────────────────────────────────

/** A resposta do banco com a chave dos filtros que a pediram — é ela que a raiz declara. */
type ListaComChave = ListaDeAvaliacoes & { filtrosMandados: string };

type Planilha =
  | { fase: "parada" }
  | { fase: "gerando"; lidas: number; total: number }
  | { fase: "feita"; linhas: number; total: number; truncado: boolean }
  | { fase: "erro"; detalhe: string };

export default function AdminAvaliacoesFeedbacks({ pedido }: { pedido: PedidoDePeriodo }) {
  const [params, setParams] = useSearchParams();
  const filtros = filtrosDaBusca(params);
  const chavePedida = chaveDosFiltros(filtros);
  const periodoPedido = chaveDoPedido(pedido);

  const { estado } = useLeitura<ListaComChave>(async (sinal) => {
    const r = await lerAvaliacoes(pedido, filtrosParaOBanco(filtros), sinal);
    return r.ok ? { ...r, filtrosMandados: chavePedida } : r;
  }, `${periodoPedido}|${chavePedida}`);

  // A ÚLTIMA lista boa. `useLeitura` troca o quadro por um erro quando a leitura
  // falha, e aqui isso apagaria a tela por causa de um filtro: um `card` que não
  // existe mais num link antigo, uma data de período fora do lugar. A lição das
  // Notas do Admin Pós (09/09) vale igual: a peça que falha é que informa, e a
  // lista anterior continua de pé, com o aviso de que é a anterior.
  const [ultimaBoa, setUltimaBoa] = useState<ListaComChave | null>(null);
  if (estado.fase === "pronto" && estado.dados !== ultimaBoa) setUltimaBoa(estado.dados);

  const dados = estado.fase === "pronto" ? estado.dados : estado.fase === "negado" ? null : ultimaBoa;
  const atualizando = estado.fase === "pronto" ? estado.atualizando : estado.fase === "carregando" && dados !== null;
  const erro = estado.fase === "erro" ? estado.detalhe : null;
  // A tela mostra a resposta ao pedido de AGORA (e não uma anterior, nem uma lista que ficou de pé depois de um erro).
  const emDia =
    estado.fase === "pronto" &&
    !estado.atualizando &&
    estado.dados.filtrosMandados === chavePedida &&
    chaveDoPeriodo(estado.dados.periodo) === periodoPedido;

  // ── A URL, lida e escrita por referência ──────────────────────────────────
  // O relógio da busca dispara 300 ms depois da tecla, e a função que ele chama
  // pode ser de um render antigo. Ela lê a URL e o campo DAQUI, e não do
  // fechamento dela — senão escreveria por cima de um filtro que mudou nesse
  // intervalo.
  const urlAtual = useRef(params);
  const mudarUrl = useRef(setParams);
  const [texto, setTexto] = useState(filtros.busca);
  const textoAtual = useRef(texto);
  useLayoutEffect(() => {
    urlAtual.current = params;
    mudarUrl.current = setParams;
    textoAtual.current = texto;
  });

  // A busca que esta tela escreveu por último na URL (ou aceitou dela). É o que
  // separa a mudança que VEIO DE FORA (o botão de voltar, um link) da que a
  // própria tela fez: sem isso, a URL chegando atrasada apagaria as letras
  // digitadas depois do relógio.
  const buscaEscrita = useRef(filtros.busca);

  // Onde a lista começa, para levar o olho até lá quando a página troca.
  const topoDaLista = useRef<HTMLDivElement>(null);
  // A chave da página que foi pedida por um gesto de paginação: quando a resposta
  // dela chegar, a tela rola até o alto da lista (e só então — rolar no clique
  // levaria o olho a uma lista que ainda é a antiga).
  const rolarAoChegar = useRef<string | null>(null);

  const [aberta, setAberta] = useState<AvaliacaoDaLista | null>(null);
  const [planilha, setPlanilha] = useState<Planilha>({ fase: "parada" });

  /**
   * Escreve os filtros na URL, com `replace`: cada letra da busca e cada troca de
   * combo não viram uma entrada no histórico (é o que o balcão e o Mapa fazem).
   * Trocar um filtro volta à página 1 — a página 3 de um filtro não é a página 3
   * de outro, e continuar nela mostraria uma lista vazia com paginação ativa.
   *
   * A busca que está no CAMPO vai junto, mesmo antes do relógio: trocar a média
   * com a busca ainda a caminho faria duas leituras, a primeira sem a busca. A
   * paginação é a exceção — ela não leva a busca pendente, porque uma busca nova
   * volta à página 1, e o relógio dela vai chegar logo depois.
   */
  function mudar(parcial: Partial<FiltrosDaTela>, manterPagina = false): FiltrosDaTela {
    const atual = filtrosDaBusca(urlAtual.current);
    const novo: FiltrosDaTela = {
      ...atual,
      ...(manterPagina ? {} : { busca: textoAtual.current.trim() }),
      ...parcial,
    };
    if (!manterPagina) {
      novo.pagina = PADRAO.pagina;
      buscaEscrita.current = novo.busca;
    }
    mudarUrl.current(buscaComFiltros(urlAtual.current, novo), { replace: true });
    return novo;
  }

  function mudarFiltro(parcial: Partial<FiltrosDaTela>) {
    rolarAoChegar.current = null;
    mudar(parcial);
  }

  function irParaPagina(pagina: number) {
    rolarAoChegar.current = chaveDosFiltros(mudar({ pagina }, true));
  }

  function mudarPorPagina(por: PorPagina) {
    // Quem troca o tamanho da página está no pé da lista, como quem pagina.
    rolarAoChegar.current = chaveDosFiltros(mudar({ por }));
  }

  function limpar() {
    textoAtual.current = "";
    setTexto("");
    mudarFiltro({ card: null, busca: "", media: "", comentario: "", ordem: PADRAO.ordem });
  }

  // URL → campo: a mudança que veio de fora (o botão de voltar, um link com
  // `busca=`) aparece no campo. A que a própria tela escreveu já está nele.
  useEffect(() => {
    if (filtros.busca !== buscaEscrita.current) {
      buscaEscrita.current = filtros.busca;
      setTexto(filtros.busca);
    }
  }, [filtros.busca]);

  // Campo → URL, depois de ESPERA_DA_BUSCA sem tecla.
  useEffect(() => {
    const limpo = texto.trim();
    if (limpo === buscaEscrita.current) return;
    const relogio = window.setTimeout(() => {
      // Um combo trocado nesse meio-tempo já levou a busca (ver `mudar`).
      if (limpo !== buscaEscrita.current) mudarFiltro({ busca: limpo });
    }, ESPERA_DA_BUSCA);
    return () => window.clearTimeout(relogio);
    // `mudarFiltro` lê só referências: quem dispara é o texto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  // A página pedida chegou: leva o olho ao alto da lista.
  const chaveMostrada = dados?.filtrosMandados ?? null;
  useEffect(() => {
    if (chaveMostrada !== null && chaveMostrada === rolarAoChegar.current) {
      rolarAoChegar.current = null;
      rolarAte(topoDaLista.current);
    }
  }, [chaveMostrada]);

  // O recado da planilha é da lista em que ela foi pedida: trocar o filtro ou o
  // período o apaga (a que estiver sendo gerada termina e diz como terminou).
  useEffect(() => {
    setPlanilha((p) => (p.fase === "gerando" || p.fase === "parada" ? p : { fase: "parada" }));
  }, [chavePedida, periodoPedido]);

  async function exportar() {
    if (!dados || planilha.fase === "gerando") return;
    setPlanilha({ fase: "gerando", lidas: 0, total: Math.min(dados.total, TETO_DA_PLANILHA) });
    const r = await planilhaDaLista(pedido, semPaginacao(filtrosParaOBanco(filtros)), (lidas, total) =>
      setPlanilha({ fase: "gerando", lidas, total }),
    );
    setPlanilha(r.ok ? { fase: "feita", linhas: r.linhas, total: r.total, truncado: r.truncado } : { fase: "erro", detalhe: r.detalhe });
  }

  const soDoPeriodo = buscaSoDoPeriodo(pedido);
  // A página da ferramenta, com o MESMO período: a média que a pessoa vê lá tem
  // de ser a destes números (o motivo de o período atravessar as sub-abas).
  const linkDaFerramenta = (slug: string) =>
    `/admin/avaliacoes/ferramentas?card=${encodeURIComponent(slug)}${soDoPeriodo ? `&${soDoPeriodo}` : ""}`;

  const mudancaNaBarra = haMudancaNaBarra(filtros) || texto.trim() !== "";

  const raiz = {
    "data-aba-das-avaliacoes": "feedbacks",
    "data-periodo-pedido": periodoPedido,
    "data-filtros-pedidos": chavePedida,
  };

  if (estado.fase === "negado") {
    return (
      <div {...raiz}>
        <AvisoDaLeitura estado={estado} />
      </div>
    );
  }
  if (!dados) {
    return (
      <div {...raiz}>
        {erro ? (
          <section className="surface p-6" role="alert" data-lista-sem-dados>
            <h2 className="font-display text-lg font-bold text-titulo">A lista não veio</h2>
            <p className="mt-2 text-sm text-cinza">{fraseSolta(erro)}</p>
            {mudancaNaBarra && (
              <button
                type="button"
                onClick={limpar}
                className="mt-3 text-sm font-semibold text-alerta-tinta underline-offset-4 hover:underline"
                data-limpar-filtros
              >
                Limpar os filtros e tentar de novo
              </button>
            )}
          </section>
        ) : (
          <AvisoDaLeitura estado={estado} />
        )}
      </div>
    );
  }

  const paginas = Math.max(1, Math.ceil(dados.total / dados.limite));
  const paginaMostrada = Math.floor(dados.offset / dados.limite) + 1;

  return (
    <div
      {...raiz}
      className="flex flex-col gap-5"
      aria-busy={atualizando}
      data-painel-pronto={!atualizando}
      data-periodo-dos-dados={chaveDoPeriodo(dados.periodo)}
      data-filtros-dos-dados={dados.filtrosMandados}
    >
      <h2 className="sr-only">Feedbacks: cada avaliação recebida no período</h2>

      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <LinhaDoPeriodo periodo={dados.periodo} atualizando={atualizando} />
          {/* "N de M": o total do FILTRO ao lado do total do período. "25 de 25"
              e "25 de 6.582" pedem trabalho diferente de quem lê — o mesmo
              motivo do cabeçalho das Notas. */}
          <p
            className="mt-1 text-sm text-cinza"
            data-contagem-da-lista
            data-total={dados.total}
            data-total-no-periodo={dados.total_no_periodo}
          >
            {dados.total_no_periodo === 0 ? (
              dados.periodo.comparacao === "sem_dado_ainda" ? AINDA_SEM_AVALIACAO : "Nenhuma avaliação de aluno no período."
            ) : (
              <>
                <strong className="font-semibold text-tinta tabular-nums">{numero(dados.total)}</strong> de{" "}
                <span className="tabular-nums">{plural(dados.total_no_periodo, "avaliação", "avaliações")}</span> no período
              </>
            )}
          </p>
        </div>
        <ExportarPlanilha
          planilha={planilha}
          // Só a lista que está NA TELA, respondendo ao pedido de agora: com a
          // leitura a caminho, o arquivo sairia com os filtros novos e a tela
          // ainda mostrando os velhos.
          desligado={!emDia || dados.total === 0}
          motivo={
            dados.total === 0
              ? "Não há avaliação a exportar com esses filtros."
              : erro
                ? "A lista na tela é a da consulta anterior: a planilha sairia diferente dela."
                : "Espere a lista terminar de carregar."
          }
          aoExportar={() => void exportar()}
        />
      </header>

      {erro && (
        <p className="rounded-xl border border-erro-borda bg-erro-fundo p-3.5 text-sm text-erro-tinta" role="alert" data-erro-da-lista>
          A lista não foi atualizada: {comPonto(erro)} O que aparece abaixo é a consulta anterior.
        </p>
      )}

      <BarraDeFiltros>
        <LinhaDeFiltros n={1}>
          {/* Sem `aoBuscar`: quem consulta é o relógio desta tela, e a lupa é só
              desenho — o modo das Notas e das Matrículas. */}
          <CampoDeBusca
            valor={texto}
            aoMudar={setTexto}
            placeholder="Comentário ou ferramenta"
            rotulo="Buscar no comentário ou no nome da ferramenta"
            aoLimpar={
              texto
                ? () => {
                    textoAtual.current = "";
                    setTexto("");
                    mudarFiltro({ busca: "" });
                  }
                : undefined
            }
            data-filtro-busca
            // As larguras mínimas: os três combos curtos têm a do próprio texto
            // mais longo (`flex-none`, e aí nunca cortam o que dizem), e quem
            // estica é a busca e a ferramenta. A busca tem 15,5rem desde 29/09: o
            // texto de exemplo é o único rótulo VISÍVEL dela, e com os 12rem de
            // antes ela mostrava "Comentário ou fer".
            // MEDIDO pela prova de tela em 30/09, com a Inter carregada: os
            // mínimos somam 1.079px (busca 248, ferramenta 184, média 185,
            // comentário 208, ordem 206 e as quatro folgas de 12) contra os cerca
            // de 1.070px da barra, que é o máximo dela, porque o contêiner para em
            // 1.152px. Então a barra QUEBRA em toda largura (a ordem desce para a
            // segunda linha), e é a quebra que mantém os textos inteiros: o texto
            // de exemplo mede 199,2px, a caixa da busca no mínimo tem 185px (248
            // menos a lupa, a margem e as bordas), e com a quebra a busca cresce e
            // a caixa passa de 300px. Se um rótulo encurtar e a barra voltar a uma
            // linha, o texto de exemplo volta a ser cortado, e quem acusa é o
            // bloco 9b da prova da Visão geral. A especificação da 000328 pedia
            // uma linha só; caber nela pede rótulos mais curtos, e essa escolha é
            // de Victor.
            className="w-full sm:w-auto sm:min-w-[15.5rem] sm:flex-[1.5]"
          />
          <Combo
            rotulo="Ferramenta"
            dado="data-filtro-ferramenta"
            valor={filtros.card ?? ""}
            aoTrocar={(v) => mudarFiltro({ card: v || null })}
            opcoes={opcoesDeFerramenta(dados, filtros.card)}
            className="w-full sm:w-auto sm:min-w-[11.5rem] sm:flex-1"
          />
          <Combo
            rotulo="Média da avaliação"
            dado="data-filtro-media"
            valor={filtros.media}
            aoTrocar={(v) => mudarFiltro({ media: MEDIAS.find((m) => m.valor === v)?.valor ?? "" })}
            opcoes={MEDIAS}
          />
          <Combo
            rotulo="Comentário"
            dado="data-filtro-comentario"
            valor={filtros.comentario}
            aoTrocar={(v) => mudarFiltro({ comentario: COMENTARIOS.find((c) => c.valor === v)?.valor ?? "" })}
            opcoes={COMENTARIOS}
          />
          {/* Ordenar não é filtrar, e fica na barra de todo modo: é o outro
              controle da MESMA lista, e quem lê procura os dois no mesmo lugar (a
              decisão das Notas). */}
          <Combo
            rotulo="Ordem da lista"
            dado="data-filtro-ordem"
            valor={filtros.ordem}
            aoTrocar={(v) => mudarFiltro({ ordem: ORDENS.find((o) => o.valor === v)?.valor ?? PADRAO.ordem })}
            opcoes={ORDENS}
          />
        </LinhaDeFiltros>
        {mudancaNaBarra && <LimparFiltros aoLimpar={limpar} texto="Limpar filtros" />}
      </BarraDeFiltros>

      {/* O que TROCA na releitura é a lista e a paginação: só elas ficam
          apagadas. A barra fica nítida — quem está digitando a busca não pode
          ver o próprio campo desbotar a cada leitura. */}
      <div className={`flex flex-col gap-4 transition-opacity ${atualizando ? "opacity-60" : ""}`}>
        {/* `relative` prende aqui dentro os `sr-only` das linhas (o nome do
            botão, "sem comentário"): com posição absoluta e nenhum ancestral
            posicionado no meio, eles escapavam da rolagem da tabela e
            esticavam a página a 375px — 721px de largura, medido pela prova de
            tela, com o diálogo saindo do centro por causa disso. */}
        <div
          ref={topoDaLista}
          className="surface relative overflow-x-auto"
          data-lista-de-avaliacoes
          data-pagina-da-lista={paginaMostrada}
        >
          {dados.itens.length > 0 ? (
            <TabelaDasAvaliacoes
              itens={dados.itens}
              ordem={filtroDaOrdem(dados.filtrosMandados)}
              linkDaFerramenta={linkDaFerramenta}
              aoAbrir={setAberta}
            />
          ) : (
            <ListaVazia
              dados={dados}
              paginas={paginas}
              comFiltro={haFiltro(filtrosDaBusca(new URLSearchParams(dados.filtrosMandados)))}
              aoLimpar={limpar}
              aoIrParaUltima={() => irParaPagina(paginas)}
            />
          )}
        </div>

        {dados.total > 0 && (
          <PaginacaoDaLista
            dados={dados}
            paginas={paginas}
            paginaMostrada={paginaMostrada}
            paginaPedida={filtros.pagina}
            por={filtros.por}
            aoIr={irParaPagina}
            aoMudarPor={mudarPorPagina}
          />
        )}
      </div>

      <p className="max-w-3xl text-xs leading-relaxed text-cinza" data-nota-da-lista>
        Só entram as avaliações de alunos: as da equipe ficam fora, como em toda conta do painel. «Dia e hora» é quando o
        aluno avaliou — ou mudou a avaliação, porque avaliar de novo substitui a anterior —, e é por esse dia que o período
        filtra. A média de cada linha é a das três notas.
      </p>

      <DialogoDaAvaliacao avaliacao={aberta} aoFechar={() => setAberta(null)} linkDaFerramenta={linkDaFerramenta} />
    </div>
  );
}

/** A ordem que o banco RESPONDEU (a da chave mandada), para o cabeçalho e a legenda não descreverem uma ordem que ainda não chegou. */
function filtroDaOrdem(chaveMandada: string): OrdemDaLista {
  return filtrosDaBusca(new URLSearchParams(chaveMandada)).ordem;
}

/**
 * As opções do seletor de ferramenta: as que o banco devolveu (as com avaliação
 * de aluno no período, e a escolhida mesmo sem nenhuma), só com o NOME.
 *
 * **Sem contagem ao lado do nome**, pela regra da barra da casa — Victor, em
 * 09/09: *"tira os números dos selects pq eles n atualizam os outros quando
 * troca um valor"*. A primeira versão desta tela (000328) mostrava "N no
 * período" e defendia a contagem como o TAMANHO da ferramenta no período, com o
 * rótulo dizendo isso com letra. O rótulo não impedia a leitura que a regra
 * existe para evitar: o número era o do período inteiro e ficava o mesmo com a
 * busca, a média e o comentário ligados — com «Só as baixas», a opção dizia as
 * avaliações da ferramenta no período, e escolhê-la mostrava só as baixas dela.
 * Quantas avaliações cada ferramenta tem no período está na Visão geral e nas
 * Ferramentas, onde nenhum filtro o muda.
 *
 * A escolhida que o banco não devolveu (um `card` de link antigo, com a leitura
 * recusada) entra com o próprio slug: sem opção para ela, o combo mostraria
 * "Todas as ferramentas" enquanto a URL filtra por uma.
 */
function opcoesDeFerramenta(dados: ListaDeAvaliacoes, escolhida: string | null): { valor: string; rotulo: string }[] {
  const opcoes = [{ valor: "", rotulo: "Todas as ferramentas" }, ...dados.ferramentas.map((f) => ({ valor: f.slug, rotulo: f.nome }))];
  if (escolhida && !dados.ferramentas.some((f) => f.slug === escolhida)) opcoes.push({ valor: escolhida, rotulo: escolhida });
  return opcoes;
}

// ── As peças ────────────────────────────────────────────────────────────────

/**
 * Um combo da barra, sem rótulo visível: o rótulo mora na primeira opção
 * ("Todas as ferramentas", "Qualquer média"), que nomeia o combo e diz o que ele
 * faz quando ninguém escolheu, e o nome acessível vai no `aria-label` — a forma
 * do `Filtro` de `BarraDeFiltros`.
 *
 * Não é o `Filtro` por dois motivos: o atributo de prova daqui é um por combo
 * (`data-filtro-media`), e o dele é `data-filtro=<chave>`; e ele ESCONDE a opção
 * com zero itens, o que sumiria com a ferramenta escolhida que não tem avaliação
 * no período — justamente a que o banco devolve de propósito.
 *
 * Sem `className`, a largura é a do conteúdo (`flex-none`): os três combos
 * curtos nunca cortam o próprio texto, e quem estica é a busca e a ferramenta.
 */
function Combo<V extends string>({
  rotulo,
  dado,
  valor,
  aoTrocar,
  opcoes,
  className = "w-full sm:w-auto sm:flex-none",
}: {
  rotulo: string;
  dado: `data-${string}`;
  valor: V;
  aoTrocar: (v: string) => void;
  opcoes: { valor: V; rotulo: string }[];
  className?: string;
}) {
  const escolhida = opcoes.find((o) => o.valor === valor);
  return (
    <select
      value={valor}
      onChange={(e) => aoTrocar(e.target.value)}
      aria-label={rotulo}
      // O texto inteiro da opção escolhida: no celular, e com um nome de
      // ferramenta longo, o combo fechado o corta.
      title={escolhida ? `${rotulo}: ${escolhida.rotulo}` : rotulo}
      {...{ [dado]: "" }}
      className={`${CAMPO_BASE} text-sm ${className}`}
    >
      {opcoes.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.rotulo}
        </option>
      ))}
    </select>
  );
}

function ExportarPlanilha({
  planilha,
  desligado,
  motivo,
  aoExportar,
}: {
  planilha: Planilha;
  desligado: boolean;
  motivo: string;
  aoExportar: () => void;
}) {
  const gerando = planilha.fase === "gerando";
  let recado = "";
  if (planilha.fase === "gerando") {
    recado = `Preparando a planilha: ${numero(planilha.lidas)} de ${plural(planilha.total, "linha", "linhas")}…`;
  } else if (planilha.fase === "feita") {
    // O teto é DITO, nunca silencioso: uma planilha cortada que parece inteira
    // é a pior forma de errar (a regra da planilha das matrículas).
    recado = planilha.truncado
      ? `Planilha baixada com ${plural(planilha.linhas, "linha", "linhas")}, e a lista tem ${numero(planilha.total)}: ${numero(
          TETO_DA_PLANILHA,
        )} é o teto da planilha. Estreite o período ou os filtros para exportar o resto.`
      : `Planilha baixada: ${plural(planilha.linhas, "linha", "linhas")}.`;
  } else if (planilha.fase === "erro") {
    recado = `A planilha não saiu: ${planilha.detalhe}`;
  }
  return (
    <div className="flex max-w-xs flex-col items-start gap-1 sm:items-end">
      <button
        type="button"
        onClick={aoExportar}
        disabled={desligado || gerando}
        title={desligado ? motivo : "Baixa um arquivo .csv, que o Excel abre"}
        className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
        data-exportar-csv
      >
        {gerando ? "Exportando…" : "Exportar CSV"}
      </button>
      {/* A dica fica FORA da região viva: ela volta a cada troca de filtro, e
          numa região viva o leitor de tela a repetiria toda vez. Só o andamento
          e o resultado são anunciados. */}
      {planilha.fase === "parada" && (
        <p className="text-xs leading-snug text-cinza sm:text-right">A lista inteira, com os filtros de agora — não só esta página.</p>
      )}
      <p
        className={`text-xs leading-snug sm:text-right ${planilha.fase === "erro" ? "text-erro-tinta" : "text-cinza"}`}
        role="status"
        data-andamento-da-planilha={planilha.fase}
        data-linhas={planilha.fase === "feita" ? planilha.linhas : undefined}
      >
        {recado}
      </p>
    </div>
  );
}

const ROTULO_DA_ORDEM: Record<OrdemDaLista, string> = {
  recentes: "as mais recentes primeiro",
  antigas: "as mais antigas primeiro",
  menor_media: "as de menor média primeiro",
  maior_media: "as de maior média primeiro",
};

function TabelaDasAvaliacoes({
  itens,
  ordem,
  linkDaFerramenta,
  aoAbrir,
}: {
  itens: AvaliacaoDaLista[];
  ordem: OrdemDaLista;
  linkDaFerramenta: (slug: string) => string;
  aoAbrir: (a: AvaliacaoDaLista) => void;
}) {
  // A ordem é do combo, e não do cabeçalho — mas quem usa leitor de tela ouve
  // no cabeçalho por qual coluna a lista está ordenada.
  const ordemDoDia = ordem === "recentes" ? "descending" : ordem === "antigas" ? "ascending" : undefined;
  const ordemDaMedia = ordem === "maior_media" ? "descending" : ordem === "menor_media" ? "ascending" : undefined;
  return (
    <table className="w-full min-w-[58rem] border-collapse text-sm">
      <caption className="sr-only">As avaliações de alunos do período, {ROTULO_DA_ORDEM[ordem]}</caption>
      <thead>
        <tr className="border-b border-borda text-left text-xs text-cinza">
          <th scope="col" className="px-4 py-2.5 font-semibold">
            Ferramenta
          </th>
          <th scope="col" className="px-2 py-2.5 text-center font-semibold">
            Utilidade
          </th>
          <th scope="col" className="px-2 py-2.5 text-center font-semibold">
            Facilidade
          </th>
          <th scope="col" className="px-2 py-2.5 text-center font-semibold">
            Satisfação
          </th>
          <th scope="col" aria-sort={ordemDaMedia} className="px-2 py-2.5 text-center font-semibold">
            Média
          </th>
          <th scope="col" className="px-3 py-2.5 font-semibold">
            Comentário
          </th>
          <th scope="col" aria-sort={ordemDoDia} className="px-3 py-2.5 font-semibold">
            Dia e hora
          </th>
          <th scope="col" className="px-4 py-2.5 font-semibold">
            Origem
          </th>
        </tr>
      </thead>
      <tbody>
        {itens.map((a) => (
          <LinhaDaAvaliacao key={a.seq} a={a} linkDaFerramenta={linkDaFerramenta} aoAbrir={aoAbrir} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * Uma avaliação. O BOTÃO do dia e da hora abre o detalhe, e é ele o caminho de
 * quem usa teclado; o clique na linha é o atalho do mouse, como no painel antigo.
 * O link da ferramenta para a propagação, para não abrir as duas coisas.
 */
function LinhaDaAvaliacao({
  a,
  linkDaFerramenta,
  aoAbrir,
}: {
  a: AvaliacaoDaLista;
  linkDaFerramenta: (slug: string) => string;
  aoAbrir: (a: AvaliacaoDaLista) => void;
}) {
  const quando = quandoLegivel(a.em);
  const comentario = a.comentario?.trim() ? a.comentario : null;

  function aoClicarNaLinha(e: MouseEvent<HTMLTableRowElement>) {
    // Quem arrastou o mouse para selecionar o comentário (e copiá-lo) não pediu
    // o diálogo.
    if (window.getSelection()?.toString()) return;
    // O foco vai para o botão da linha ANTES de abrir: é a quem tem o foco que o
    // diálogo o devolve ao fechar, e a linha não recebe foco — sem isto, quem
    // abriu pelo mouse e fecha com Esc cairia no começo da página.
    e.currentTarget.querySelector<HTMLButtonElement>("[data-abrir-avaliacao]")?.focus({ preventScroll: true });
    aoAbrir(a);
  }

  return (
    <tr
      onClick={aoClicarNaLinha}
      className="cursor-pointer border-b border-borda/60 transition-colors last:border-0 hover:bg-superficie-2"
      data-avaliacao={a.seq}
      data-media={a.media}
    >
      <th scope="row" className="px-4 py-2.5 text-left font-normal">
        <Link
          to={linkDaFerramenta(a.card)}
          onClick={(e) => e.stopPropagation()}
          className="font-semibold text-link underline-offset-2 hover:underline"
          data-ferramenta-da-linha={a.card}
        >
          {a.nome}
        </Link>
      </th>
      <td className="px-2 py-2.5 text-center tabular-nums text-tinta" data-nota="utilidade">
        {a.utilidade}
      </td>
      <td className="px-2 py-2.5 text-center tabular-nums text-tinta" data-nota="facilidade">
        {a.facilidade}
      </td>
      <td className="px-2 py-2.5 text-center tabular-nums text-tinta" data-nota="satisfacao">
        {a.satisfacao}
      </td>
      <td className="px-2 py-2.5 text-center">
        <SeloDaMedia media={a.media} />
      </td>
      <td className="px-3 py-2.5 text-tinta">
        {comentario ? (
          // Uma linha, cortada, com o texto inteiro no `title`: o comentário
          // inteiro está no diálogo, e a lista é para achar, não para ler. O
          // corte mora num bloco com largura máxima porque `max-width` numa
          // célula de tabela não é respeitado de forma confiável.
          <span className="block max-w-[22rem] truncate" title={comentario} data-comentario-da-linha>
            {comentario}
          </span>
        ) : (
          <span className="text-cinza">
            <span aria-hidden="true">—</span>
            <span className="sr-only">sem comentário</span>
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // O Safari não dá foco ao botão no clique; o diálogo devolve o foco a
            // quem o tinha, então ele tem de estar aqui.
            e.currentTarget.focus();
            aoAbrir(a);
          }}
          className="text-left tabular-nums text-link"
          data-abrir-avaliacao={a.seq}
        >
          <span className="sr-only">Ver a avaliação de {a.nome}: </span>
          <span className="block underline decoration-link/40 underline-offset-2">{quando.dia}</span>
          <span className="block text-xs">
            <span className="sr-only">{artigoDaHora(quando.hora)} </span>
            {quando.hora}
          </span>
        </button>
      </td>
      <td
        className="px-4 py-2.5 whitespace-nowrap text-cinza"
        title={a.origem === "hub" ? "dada no hub" : "importada do app antigo, a Central Feedbacks"}
        data-origem={a.origem}
      >
        {ROTULO_DA_ORIGEM[a.origem]}
      </td>
    </tr>
  );
}

/**
 * A lista sem linha nenhuma, e as três razões dizem coisas diferentes: o período
 * não tem avaliação (limpar os filtros não ajudaria), os filtros não deixaram
 * nenhuma (limpar ajuda), ou a página pedida passou do fim — um link antigo, ou
 * um período que encolheu —, e aí "nenhuma avaliação" seria falso.
 */
function ListaVazia({
  dados,
  paginas,
  comFiltro,
  aoLimpar,
  aoIrParaUltima,
}: {
  dados: ListaDeAvaliacoes;
  paginas: number;
  comFiltro: boolean;
  aoLimpar: () => void;
  aoIrParaUltima: () => void;
}) {
  let texto: string;
  let acao: ReactNode = null;
  if (dados.total > 0) {
    texto = `Esta página não existe nesta lista: são ${plural(dados.total, "avaliação", "avaliações")}, em ${plural(
      paginas,
      "página",
      "páginas",
    )}.`;
    acao = (
      <button type="button" onClick={aoIrParaUltima} className="btn-contorno text-sm" data-ir-para-a-ultima-pagina>
        Ir para a página {numero(paginas)}
      </button>
    );
  } else if (dados.total_no_periodo === 0) {
    // O cabeçalho já diz "nenhuma avaliação de aluno no período"; aqui vai o
    // que fazer com isso, e não a mesma frase de novo.
    texto = "Sem avaliação para mostrar. Escolha um período maior na barra acima.";
  } else {
    texto = "Nenhuma avaliação com esses filtros no período.";
    if (comFiltro) {
      acao = (
        <button
          type="button"
          onClick={aoLimpar}
          className="text-sm font-semibold text-alerta-tinta underline-offset-4 hover:underline"
          data-limpar-filtros-do-vazio
        >
          Limpar filtros
        </button>
      );
    }
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 p-8 text-center" data-lista-vazia>
      <p className="text-sm text-cinza" role="status">
        {texto}
      </p>
      {acao}
    </div>
  );
}

/**
 * A paginação DIZ o total e onde se está: "26–50 de 120 · página 2 de 5"
 * responde "quantos faltam" sem conta de cabeça. Os números saem da RESPOSTA
 * (o deslocamento e o limite que o banco devolveu), porque é a página que está
 * na tela; o botão anda a partir da página PEDIDA, para dois cliques seguidos
 * andarem duas páginas — quem vale é a última resposta (`useLeitura`).
 *
 * Os botões não se desligam durante a leitura. Pela regra de correção de foco do
 * HTML, o botão com o foco que se desliga deixa de ser focável e o foco vai para
 * a página; no navegador que a segue, quem pagina pelo teclado voltaria ao
 * começo a cada clique. Desligam só nas pontas, onde não há para onde ir.
 */
function PaginacaoDaLista({
  dados,
  paginas,
  paginaMostrada,
  paginaPedida,
  por,
  aoIr,
  aoMudarPor,
}: {
  dados: ListaDeAvaliacoes;
  paginas: number;
  paginaMostrada: number;
  paginaPedida: number;
  por: PorPagina;
  aoIr: (pagina: number) => void;
  aoMudarPor: (por: PorPagina) => void;
}) {
  const primeiro = dados.offset + 1;
  const ultimo = dados.offset + dados.itens.length;
  return (
    <nav className="flex flex-wrap items-center justify-between gap-3" aria-label="Páginas da lista de avaliações" data-paginacao-da-lista>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => aoIr(Math.min(paginaPedida - 1, paginas))}
          disabled={paginaPedida <= 1}
          className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          data-pagina-anterior
        >
          ← Anteriores
        </button>
        <span className="text-xs tabular-nums text-cinza" role="status" data-pagina-atual={paginaMostrada} data-paginas={paginas}>
          {dados.itens.length > 0 ? `${numero(primeiro)}–${numero(ultimo)} de ${numero(dados.total)} · ` : ""}
          página {numero(paginaMostrada)} de {numero(paginas)}
        </span>
        <button
          type="button"
          onClick={() => aoIr(paginaPedida + 1)}
          disabled={paginaPedida >= paginas}
          className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          data-pagina-proxima
        >
          Próximas →
        </button>
      </div>
      <label className="flex items-center gap-2 text-xs font-semibold text-cinza">
        Por página
        <select
          value={por}
          onChange={(e) => aoMudarPor(POR_PAGINA.find((n) => n === Number(e.target.value)) ?? PADRAO.por)}
          className={`${CAMPO_BASE} w-24 text-sm`}
          data-por-pagina
        >
          {POR_PAGINA.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}
